import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  Platform,
  Modal,
  ActivityIndicator,
  Alert,
  Animated,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import StudentAvatar from '../components/StudentAvatar';
import Stopwatch from '../components/Stopwatch';
import RoutineExerciseTimeline from '../components/RoutineExerciseTimeline';
import RoutineExercisePicker from '../components/RoutineExercisePicker';
import { getApiUrl } from '../utils/apiConfig';
import {
  fetchExerciseRoutines,
  fetchSharedExerciseRoutines,
  fetchRoutineItems,
  createExerciseRoutine,
  updateExerciseRoutine,
  batchUpdateRoutineNames,
  batchDeleteRoutines,
  syncRoutineItems,
  shareExerciseRoutine,
  cloneExerciseRoutine,
} from '../utils/exerciseRoutinesApi';
import {
  CLONED_ROUTINE_PREFIX,
  normalizeClonedRoutineName,
  clonedRoutineSuffixFromName,
} from '../utils/routineCloneName';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';
import {
  buildExerciseTypesFromRoutineItems,
  flattenExerciseTypesToRoutineItems,
} from '../utils/routineTimelineMapping';

function allTypeIds(exerciseTypes) {
  return new Set((exerciseTypes || []).map((t) => t.exercise_type_id));
}

export default function RoutineBuilder({ navigation, route }) {
  const theme = useTheme();
  const { weightInKg } = useUserPreferences();
  const styles = getStyles(theme);
  const stopwatchRef = useRef(null);
  const scrollRef = useRef(null);
  const routineOffsetsRef = useRef({});
  const highlightOpacity = useRef(new Animated.Value(0)).current;
  const [highlightRoutineId, setHighlightRoutineId] = useState(null);

  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [routines, setRoutines] = useState([]);
  const [sharedRoutines, setSharedRoutines] = useState([]);
  const [shareStudents, setShareStudents] = useState([]);
  const [loadingShareStudents, setLoadingShareStudents] = useState(false);
  const [shareTargetRoutine, setShareTargetRoutine] = useState(null);
  const [shareRecipientId, setShareRecipientId] = useState(null);
  const [showShareStudentDropdown, setShowShareStudentDropdown] = useState(false);
  const [sharingRoutine, setSharingRoutine] = useState(false);
  const [shareSucceeded, setShareSucceeded] = useState(false);
  const [shareModalToast, setShareModalToast] = useState(null);
  const [saveToast, setSaveToast] = useState(null);
  const [cloneTargetRoutine, setCloneTargetRoutine] = useState(null);
  const [cloneNameSuffix, setCloneNameSuffix] = useState('');
  const [cloningRoutine, setCloningRoutine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listMode, setListMode] = useState('view');
  const [renameDrafts, setRenameDrafts] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingNames, setSavingNames] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedByRoutine, setExpandedByRoutine] = useState({});

  const [activeEdit, setActiveEdit] = useState(null);
  const [editExpandedTypes, setEditExpandedTypes] = useState(new Set());
  const [editExpandedExercises, setEditExpandedExercises] = useState(new Set());
  const [savingRoutine, setSavingRoutine] = useState(false);

  const studentId = currentUser?.id || route?.params?.userId;
  const canShareRoutines = isFirstDegreeOrHigher(currentUser);

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetch(`${getApiUrl()}/api/users/${userId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
  }, [route?.params?.userId]);

  const loadShareStudents = useCallback(async () => {
    if (!canShareRoutines || !studentId) return;
    setLoadingShareStudents(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users?visible=true&requester_id=${encodeURIComponent(String(studentId))}`
      );
      if (res.ok) {
        const data = await res.json();
        setShareStudents(
          (Array.isArray(data) ? data : []).filter((s) => Number(s.id) !== Number(studentId))
        );
      }
    } catch {
      setShareStudents([]);
    } finally {
      setLoadingShareStudents(false);
    }
  }, [canShareRoutines, studentId]);

  useEffect(() => {
    void loadShareStudents();
  }, [loadShareStudents]);

  const loadRoutines = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const ownedList = await fetchExerciseRoutines(studentId);
      let sharedList = [];
      try {
        sharedList = await fetchSharedExerciseRoutines(studentId);
      } catch (sharedError) {
        console.warn('Could not load shared routines:', sharedError);
      }
      const withItems = await Promise.all(
        ownedList.map(async (routine) => {
          try {
            const items = await fetchRoutineItems(routine.id, { accessStudentId: studentId });
            const exerciseTypes = buildExerciseTypesFromRoutineItems(items);
            return { ...routine, is_owned: true, exerciseTypes, itemsError: null };
          } catch {
            return { ...routine, is_owned: true, exerciseTypes: [], itemsError: 'Failed to load exercises' };
          }
        })
      );
      const sharedWithItems = await Promise.all(
        sharedList.map(async (routine) => {
          try {
            const items = await fetchRoutineItems(routine.id, { accessStudentId: studentId });
            const exerciseTypes = buildExerciseTypesFromRoutineItems(items);
            return { ...routine, is_owned: false, exerciseTypes, itemsError: null };
          } catch {
            return { ...routine, is_owned: false, exerciseTypes: [], itemsError: 'Failed to load exercises' };
          }
        })
      );
      setRoutines(withItems);
      setSharedRoutines(sharedWithItems);
      const expanded = {};
      [...withItems, ...sharedWithItems].forEach((r) => {
        expanded[r.id] = {
          types: allTypeIds(r.exerciseTypes),
          exercises: new Set(),
        };
      });
      setExpandedByRoutine(expanded);
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to load routines.');
      setRoutines([]);
      setSharedRoutines([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    void loadRoutines();
  }, [loadRoutines]);

  useEffect(() => {
    const paramId = route?.params?.highlightRoutineId;
    if (paramId == null || paramId === '') return;
    const parsedId = Number(paramId);
    if (!Number.isFinite(parsedId)) return;
    setHighlightRoutineId(parsedId);
    if (navigation?.setParams) {
      navigation.setParams({ highlightRoutineId: undefined });
    }
  }, [route?.params?.highlightRoutineId, navigation]);

  useEffect(() => {
    if (highlightRoutineId == null || loading) return;

    const timer = setTimeout(() => {
      const y = routineOffsetsRef.current[highlightRoutineId];
      if (y != null && scrollRef.current?.scrollTo) {
        scrollRef.current.scrollTo({ y: Math.max(0, y - 24), animated: true });
      }

      highlightOpacity.stopAnimation();
      highlightOpacity.setValue(0.8);
      Animated.timing(highlightOpacity, {
        toValue: 0,
        duration: 1000,
        useNativeDriver: Platform.OS !== 'web',
      }).start(({ finished }) => {
        if (finished) setHighlightRoutineId(null);
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [highlightRoutineId, loading, routines, sharedRoutines, highlightOpacity]);

  const enterRenameMode = () => {
    setListMode('rename');
    setSelectedForDelete(new Set());
    const drafts = {};
    routines.forEach((r) => {
      drafts[r.id] = r.name;
    });
    setRenameDrafts(drafts);
  };

  const reverseRenameChanges = () => {
    setListMode('view');
    setRenameDrafts({});
  };

  const handleUpdateNames = async () => {
    const updates = routines
      .filter((r) => renameDrafts[r.id] != null && renameDrafts[r.id].trim() !== r.name)
      .map((r) => ({ id: r.id, name: renameDrafts[r.id].trim() }));

    if (updates.length === 0) {
      setListMode('view');
      return;
    }

    setSavingNames(true);
    try {
      await batchUpdateRoutineNames(updates, studentId);
      setRoutines((prev) =>
        prev.map((r) => {
          const u = updates.find((x) => x.id === r.id);
          return u ? { ...r, name: u.name } : r;
        })
      );
      setListMode('view');
      setRenameDrafts({});
    } catch {
      Alert.alert('Error', 'Failed to update routine names.');
    } finally {
      setSavingNames(false);
    }
  };

  const toggleDeleteMode = () => {
    if (listMode === 'delete') {
      setListMode('view');
      setSelectedForDelete(new Set());
      return;
    }
    setListMode('delete');
    setRenameDrafts({});
  };

  const toggleRoutineSelected = (routineId) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(routineId)) next.delete(routineId);
      else next.add(routineId);
      return next;
    });
  };

  const handleDeleteIconPress = () => {
    if (listMode !== 'delete') {
      toggleDeleteMode();
      return;
    }
    if (selectedForDelete.size === 0) {
      Alert.alert('Select routines', 'Select one or more routines to delete.');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await batchDeleteRoutines(Array.from(selectedForDelete), studentId);
      setRoutines((prev) => prev.filter((r) => !selectedForDelete.has(r.id)));
      setSelectedForDelete(new Set());
      setListMode('view');
      setShowDeleteModal(false);
      if (activeEdit && selectedForDelete.has(activeEdit.routineId)) {
        setActiveEdit(null);
      }
    } catch {
      Alert.alert('Error', 'Failed to delete routines.');
    } finally {
      setDeleting(false);
    }
  };

  const startNewRoutine = () => {
    setListMode('view');
    setActiveEdit({ routineId: 'new', name: '', exerciseTypes: [] });
    setEditExpandedTypes(new Set());
    setEditExpandedExercises(new Set());
  };

  const startEditExercises = (routine) => {
    setListMode('view');
    setActiveEdit({
      routineId: routine.id,
      name: routine.name,
      exerciseTypes: JSON.parse(JSON.stringify(routine.exerciseTypes || [])),
    });
    setEditExpandedTypes(allTypeIds(routine.exerciseTypes));
    setEditExpandedExercises(new Set());
  };

  const cancelEdit = () => {
    setActiveEdit(null);
  };

  const mergeExercisesIntoEdit = (addedExercises) => {
    if (!activeEdit || !addedExercises?.length) return;
    setActiveEdit((prev) => {
      const exerciseTypes = JSON.parse(JSON.stringify(prev.exerciseTypes || []));
      addedExercises.forEach((withPerformance) => {
        const typeId = withPerformance.exercise_type_id;
        const idx = exerciseTypes.findIndex((t) => t.exercise_type_id === typeId);
        if (idx >= 0) {
          exerciseTypes[idx].exercises.push(withPerformance);
        } else {
          exerciseTypes.push({
            id: Date.now() + Math.random(),
            exercise_type_id: typeId,
            exercise_type_name: withPerformance.exercise_type || 'Exercise',
            exercises: [withPerformance],
          });
        }
      });
      return { ...prev, exerciseTypes };
    });
    setEditExpandedTypes((prev) => {
      const next = new Set(prev);
      addedExercises.forEach((ex) => next.add(ex.exercise_type_id));
      return next;
    });
  };

  const removeExerciseFromEdit = (exerciseId) => {
    setActiveEdit((prev) => {
      const exerciseTypes = (prev.exerciseTypes || [])
        .map((typeGroup) => ({
          ...typeGroup,
          exercises: (typeGroup.exercises || []).filter((ex) => ex.id !== exerciseId),
        }))
        .filter((typeGroup) => typeGroup.exercises.length > 0);
      return { ...prev, exerciseTypes };
    });
  };

  const saveActiveRoutine = async () => {
    if (!activeEdit?.name?.trim()) {
      Alert.alert('Name required', 'Enter a routine name before saving.');
      return;
    }
    const items = flattenExerciseTypesToRoutineItems(activeEdit.exerciseTypes);
    if (items.length === 0) {
      Alert.alert('Exercises required', 'Add at least one exercise to this routine.');
      return;
    }

    setSavingRoutine(true);
    try {
      let routineId = activeEdit.routineId;
      if (routineId === 'new') {
        const created = await createExerciseRoutine({
          studentId,
          name: activeEdit.name.trim(),
        });
        routineId = created.id;
      } else {
        const existing = routines.find((r) => r.id === routineId);
        if (existing && existing.name !== activeEdit.name.trim()) {
          await updateExerciseRoutine(routineId, {
            name: activeEdit.name.trim(),
            ownerStudentId: studentId,
          });
        }
      }
      await syncRoutineItems(routineId, items, studentId);
      setActiveEdit(null);
      await loadRoutines();
      Alert.alert('Saved', 'Routine saved successfully.');
    } catch {
      Alert.alert('Error', 'Failed to save routine.');
    } finally {
      setSavingRoutine(false);
    }
  };

  const toggleRoutineTypeExpanded = (routineId, typeId) => {
    setExpandedByRoutine((prev) => {
      const current = prev[routineId] || { types: new Set(), exercises: new Set() };
      const types = new Set(current.types);
      if (types.has(typeId)) types.delete(typeId);
      else types.add(typeId);
      return { ...prev, [routineId]: { ...current, types } };
    });
  };

  const toggleRoutineExerciseExpanded = (routineId, exerciseId) => {
    setExpandedByRoutine((prev) => {
      const current = prev[routineId] || { types: new Set(), exercises: new Set() };
      const exercises = new Set(current.exercises);
      if (exercises.has(exerciseId)) exercises.delete(exerciseId);
      else exercises.add(exerciseId);
      return { ...prev, [routineId]: { ...current, exercises } };
    });
  };

  const openSharePanel = (routine) => {
    setShareTargetRoutine(routine);
    setShareRecipientId(null);
    setShowShareStudentDropdown(false);
    setShareSucceeded(false);
    setShareModalToast(null);
  };

  const closeSharePanel = () => {
    if (sharingRoutine) return;
    setShareTargetRoutine(null);
    setShareRecipientId(null);
    setShowShareStudentDropdown(false);
    setShareSucceeded(false);
    setShareModalToast(null);
  };

  const handleShareRoutineConfirm = async () => {
    if (!shareTargetRoutine?.id || !shareRecipientId || !studentId) {
      setShareModalToast({ type: 'error', message: 'Select a student to share with.' });
      return;
    }
    setSharingRoutine(true);
    setShareModalToast(null);
    try {
      const result = await shareExerciseRoutine({
        routineId: shareTargetRoutine.id,
        sharedWithStudentId: shareRecipientId,
        sharedByStudentId: studentId,
      });
      setShareModalToast({
        type: 'success',
        message: `Routine shared with ${result.recipient_name || 'student'}.`,
      });
      setShareSucceeded(true);
      setShowShareStudentDropdown(false);
    } catch (error) {
      setShareModalToast({
        type: 'error',
        message: error.message || 'Failed to share routine.',
      });
    } finally {
      setSharingRoutine(false);
    }
  };

  const openCloneModal = (routine) => {
    setCloneTargetRoutine(routine);
    setCloneNameSuffix(clonedRoutineSuffixFromName(routine.name));
  };

  const closeCloneModal = () => {
    if (cloningRoutine) return;
    setCloneTargetRoutine(null);
    setCloneNameSuffix('');
  };

  const handleCloneRoutineConfirm = async () => {
    if (!cloneTargetRoutine?.id || !studentId) return;
    setCloningRoutine(true);
    setSaveToast(null);
    try {
      const name = normalizeClonedRoutineName(cloneNameSuffix || cloneTargetRoutine.name);
      await cloneExerciseRoutine({
        routineId: cloneTargetRoutine.id,
        studentId,
        name,
      });
      setSaveToast({ type: 'success', message: 'Routine cloned to My Routines.' });
      closeCloneModal();
      await loadRoutines();
    } catch (error) {
      setSaveToast({
        type: 'error',
        message: error.message || 'Failed to clone routine.',
      });
    } finally {
      setCloningRoutine(false);
    }
  };

  const renderRoutineTitleCell = (routine, titleText, titleStyle, numberOfLines) => {
    const isHighlighted = highlightRoutineId === routine.id;
    return (
      <View
        style={styles.routineTitleCell}
        {...(Platform.OS === 'web' ? { dataSet: { routineBuilderTitleCell: 'true' } } : {})}
      >
        {isHighlighted ? (
          <Animated.View
            pointerEvents="none"
            style={[styles.routineTitleHighlight, { opacity: highlightOpacity }]}
          />
        ) : null}
        <Text style={titleStyle} numberOfLines={numberOfLines}>
          {titleText}
        </Text>
      </View>
    );
  };

  const renderRoutineCard = (routine, { isShared = false } = {}) => {
    const expanded = expandedByRoutine[routine.id] || {
      types: new Set(),
      exercises: new Set(),
    };
    const isSelected = selectedForDelete.has(routine.id);
    const ownerName = routine.owner_student_name || 'Student';

    return (
      <View
        key={`${isShared ? 'shared' : 'owned'}-${routine.id}`}
        style={styles.routineCard}
        onLayout={(event) => {
          routineOffsetsRef.current[routine.id] = event.nativeEvent.layout.y;
        }}
      >
        <View style={styles.routineBox}>
          {isShared ? (
            <View style={styles.sharedRoutineHeader}>
              <View style={styles.sharedRoutineTitleRow}>
                <StudentAvatar
                  studentId={routine.owner_student_id || routine.student_id}
                  studentName={ownerName}
                  profilePictureUrl={routine.owner_profile_picture_url}
                  webDataSet={{ routineBuilderSharedAvatar: 'true' }}
                />
                <Text style={styles.sharedRoutineBadge}>Shared Routine</Text>
              </View>
              <View style={styles.sharedRoutineMetaRow}>
                {renderRoutineTitleCell(
                  routine,
                  routine.name,
                  styles.routineNameInline,
                  2
                )}
                <Text style={styles.sharedRoutineOwnerInline} numberOfLines={1}>
                  [{ownerName}]
                </Text>
              </View>
            </View>
          ) : null}
          <View style={styles.routineCardHeader}>
            {!isShared && listMode === 'delete' ? (
              <TouchableOpacity
                style={styles.routineCheckboxWrap}
                onPress={() => toggleRoutineSelected(routine.id)}
              >
                <View
                  style={[styles.routineCheckbox, isSelected && styles.routineCheckboxSelected]}
                >
                  {isSelected ? <Text style={styles.checkmark}>✓</Text> : null}
                </View>
              </TouchableOpacity>
            ) : null}
            {!isShared && listMode === 'rename' ? (
              <TextInput
                style={styles.routineNameInput}
                value={renameDrafts[routine.id] ?? routine.name}
                onChangeText={(text) =>
                  setRenameDrafts((prev) => ({ ...prev, [routine.id]: text }))
                }
              />
            ) : !isShared ? (
              renderRoutineTitleCell(routine, routine.name, styles.routineName)
            ) : null}
            {listMode === 'view' && !isShared ? (
              <>
                {canShareRoutines ? (
                  <TouchableOpacity
                    style={styles.iconActionBtn}
                    onPress={() => openSharePanel(routine)}
                    accessibilityLabel={`Share ${routine.name}`}
                  >
                    <MaterialIcons
                      name="share"
                      size={20}
                      color={theme.colors.primary || '#d4af37'}
                    />
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  style={styles.editExercisesBtn}
                  onPress={() => startEditExercises(routine)}
                  accessibilityLabel={`Edit exercises for ${routine.name}`}
                  accessibilityRole="button"
                >
                  <MaterialIcons
                    name="edit"
                    size={22}
                    color={theme.colors.primary || '#d4af37'}
                  />
                </TouchableOpacity>
              </>
            ) : null}
            {listMode === 'view' && isShared ? (
              <TouchableOpacity
                style={styles.cloneBtn}
                onPress={() => openCloneModal(routine)}
                accessibilityLabel={`Clone ${routine.name}`}
              >
                <MaterialIcons name="content-copy" size={18} color="#FFFFFF" />
                <Text style={styles.cloneBtnText}>Clone</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {routine.itemsError ? (
            <Text style={styles.emptyListText}>{routine.itemsError}</Text>
          ) : (
            <RoutineExerciseTimeline
              theme={theme}
              exerciseTypes={routine.exerciseTypes}
              expandedExerciseTypes={expanded.types}
              onToggleExerciseType={(typeId) => toggleRoutineTypeExpanded(routine.id, typeId)}
              expandedExerciseIds={expanded.exercises}
              onToggleExerciseExpanded={(exerciseId) =>
                toggleRoutineExerciseExpanded(routine.id, exerciseId)
              }
              studentId={studentId}
              weightInKg={weightInKg}
              emptyText="No exercises in this routine."
            />
          )}
        </View>
      </View>
    );
  };

  const renderRenameButtons = () => (
    <View style={styles.renameActions}>
      <TouchableOpacity
        style={[styles.secondaryBtn, savingNames && styles.btnDisabled]}
        onPress={reverseRenameChanges}
        disabled={savingNames}
      >
        <Text style={styles.secondaryBtnText}>Reverse Changes</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.primaryBtn, savingNames && styles.btnDisabled]}
        onPress={handleUpdateNames}
        disabled={savingNames}
      >
        <Text style={styles.primaryBtnText}>{savingNames ? 'Updating…' : 'Update'}</Text>
      </TouchableOpacity>
    </View>
  );

  const selectedDeleteNames = routines
    .filter((r) => selectedForDelete.has(r.id))
    .map((r) => r.name);

  return (
    <View
      style={styles.container}
      data-screen="routine-builder"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Routine Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.pageTitle}>Routine Builder</Text>

        {saveToast ? (
          <View
            style={[
              styles.feedbackToast,
              saveToast.type === 'error' ? styles.feedbackToastError : styles.feedbackToastSuccess,
            ]}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    routineBuilderToast: 'true',
                    routineBuilderToastType: saveToast.type,
                  },
                }
              : {})}
          >
            <Text
              style={styles.feedbackToastMessage}
              {...(Platform.OS === 'web' ? { dataSet: { routineBuilderToastText: 'true' } } : {})}
            >
              {saveToast.message}
            </Text>
            <TouchableOpacity
              onPress={() => setSaveToast(null)}
              style={styles.feedbackToastClose}
              hitSlop={12}
            >
              <Text style={styles.feedbackToastCloseText}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {activeEdit ? (
          <View style={styles.editPanel}>
            <Text style={styles.editPanelTitle}>
              {activeEdit.routineId === 'new' ? 'Create new routine' : 'Edit routine exercises'}
            </Text>
            <Text style={styles.label}>Routine name</Text>
            <TextInput
              style={styles.nameInput}
              value={activeEdit.name}
              onChangeText={(text) => setActiveEdit((prev) => ({ ...prev, name: text }))}
              placeholder="Routine name"
              placeholderTextColor={theme.colors.textSecondary || '#888'}
            />
            <View style={styles.routineBox}>
              <RoutineExerciseTimeline
                theme={theme}
                exerciseTypes={activeEdit.exerciseTypes}
                expandedExerciseTypes={editExpandedTypes}
                onToggleExerciseType={(typeId) => {
                  setEditExpandedTypes((prev) => {
                    const next = new Set(prev);
                    if (next.has(typeId)) next.delete(typeId);
                    else next.add(typeId);
                    return next;
                  });
                }}
                expandedExerciseIds={editExpandedExercises}
                onToggleExerciseExpanded={(exerciseId) => {
                  setEditExpandedExercises((prev) => {
                    const next = new Set(prev);
                    if (next.has(exerciseId)) next.delete(exerciseId);
                    else next.add(exerciseId);
                    return next;
                  });
                }}
                editable
                onRemoveExercise={removeExerciseFromEdit}
                studentId={studentId}
                weightInKg={weightInKg}
                emptyText="No exercises yet. Use the tiles below to add exercises."
              />
            </View>
            <RoutineExercisePicker
              theme={theme}
              studentId={studentId}
              onExercisesAdded={mergeExercisesIntoEdit}
            />
            <View style={styles.editActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={cancelEdit}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryBtn, savingRoutine && styles.btnDisabled]}
                onPress={saveActiveRoutine}
                disabled={savingRoutine}
              >
                <Text style={styles.primaryBtnText}>
                  {savingRoutine ? 'Saving…' : 'Save Routine'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>My Routines</Text>
          <View style={styles.sectionActions}>
            <TouchableOpacity
              style={[styles.iconBtn, listMode === 'rename' && styles.iconBtnActive]}
              onPress={listMode === 'rename' ? reverseRenameChanges : enterRenameMode}
              accessibilityLabel="Edit routine names"
            >
              <MaterialIcons
                name="edit"
                size={22}
                color={listMode === 'rename' ? '#FFFFFF' : theme.colors.primary || '#d4af37'}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.iconBtn, listMode === 'delete' && styles.iconBtnActive]}
              onPress={handleDeleteIconPress}
              accessibilityLabel="Delete routines"
            >
              <MaterialIcons
                name="delete-outline"
                size={22}
                color={listMode === 'delete' ? '#FFFFFF' : theme.colors.primary || '#d4af37'}
              />
            </TouchableOpacity>
            <TouchableOpacity style={styles.newRoutineBtn} onPress={startNewRoutine}>
              <Text style={styles.newRoutineBtnText}>+ New Routine</Text>
            </TouchableOpacity>
          </View>
        </View>

        {listMode === 'rename' ? renderRenameButtons() : null}

        {loading ? (
          <ActivityIndicator color={theme.colors.primary || '#d4af37'} style={styles.loader} />
        ) : routines.length === 0 ? (
          <Text style={styles.emptyListText}>
            No routines yet. Tap + New Routine to create one.
          </Text>
        ) : (
          routines.map((routine) => renderRoutineCard(routine, { isShared: false }))
        )}

        {listMode === 'rename' && routines.length > 0 ? renderRenameButtons() : null}

        {sharedRoutines.length > 0 ? (
          <>
            <View style={[styles.sectionHeader, styles.sharedSectionHeader]}>
              <Text style={styles.sectionTitle}>Shared Routines</Text>
            </View>
            {sharedRoutines.map((routine) => renderRoutineCard(routine, { isShared: true }))}
          </>
        ) : null}
      </ScrollView>

      {shareTargetRoutine ? (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={closeSharePanel}
        >
          <View
            style={styles.modalOverlay}
            {...(Platform.OS === 'web' ? { dataSet: { routineBuilderShareModalOverlay: 'true' } } : {})}
          >
            <TouchableOpacity
              style={styles.modalOutsideTap}
              activeOpacity={1}
              onPress={closeSharePanel}
              disabled={sharingRoutine}
              accessibilityLabel="Dismiss share routine dialog"
            />
            <View
              style={styles.shareModalCard}
              onStartShouldSetResponder={() => true}
              {...(Platform.OS === 'web' ? { dataSet: { routineBuilderShareModal: 'true' } } : {})}
            >
              <View style={styles.shareModalHeader}>
                <Text
                  style={styles.shareModalTitle}
                  {...(Platform.OS === 'web' ? { dataSet: { routineBuilderShareModalTitle: 'true' } } : {})}
                >
                  Share “{shareTargetRoutine.name}”
                </Text>
                <TouchableOpacity
                  style={styles.shareModalCloseIcon}
                  onPress={closeSharePanel}
                  disabled={sharingRoutine}
                  hitSlop={12}
                  accessibilityLabel="Close share routine dialog"
                >
                  <Text style={styles.shareModalCloseIconText}>✕</Text>
                </TouchableOpacity>
              </View>

              <Text style={styles.label}>Share with student</Text>
              <TouchableOpacity
                style={styles.shareDropdownTrigger}
                onPress={() => setShowShareStudentDropdown((v) => !v)}
                disabled={loadingShareStudents || sharingRoutine || shareSucceeded}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                {loadingShareStudents ? (
                  <ActivityIndicator size="small" color={theme.colors.primary || '#d4af37'} />
                ) : (
                  <>
                    <Text style={styles.shareDropdownTriggerText} numberOfLines={1}>
                      {shareRecipientId
                        ? shareStudents.find((s) => s.id === shareRecipientId)?.student_name ||
                          shareStudents.find((s) => s.id === shareRecipientId)?.username ||
                          'Selected student'
                        : 'Select student…'}
                    </Text>
                    <Text style={styles.shareDropdownArrow}>
                      {showShareStudentDropdown ? '▲' : '▼'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
              {showShareStudentDropdown && shareStudents.length > 0 ? (
                <ScrollView
                  style={styles.shareDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {shareStudents.map((s) => (
                    <TouchableOpacity
                      key={s.id}
                      style={[
                        styles.shareDropdownItem,
                        shareRecipientId === s.id && styles.shareDropdownItemSelected,
                      ]}
                      onPress={() => {
                        setShareRecipientId(s.id);
                        setShowShareStudentDropdown(false);
                      }}
                      disabled={shareSucceeded}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text style={styles.shareDropdownItemText}>
                        {s.student_name || s.username}
                        {s.student_grade ? ` · ${s.student_grade}` : ''}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : null}

              <View style={styles.sharePanelActions}>
                {!shareSucceeded ? (
                  <>
                    <TouchableOpacity
                      style={styles.secondaryBtn}
                      onPress={closeSharePanel}
                      disabled={sharingRoutine}
                    >
                      <Text style={styles.secondaryBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.primaryBtn, sharingRoutine && styles.btnDisabled]}
                      onPress={() => void handleShareRoutineConfirm()}
                      disabled={sharingRoutine || !shareRecipientId}
                    >
                      <Text style={styles.primaryBtnText}>
                        {sharingRoutine ? 'Sharing…' : 'Share'}
                      </Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <TouchableOpacity style={styles.shareCloseBtn} onPress={closeSharePanel}>
                    <Text style={styles.shareCloseBtnText}>Close</Text>
                  </TouchableOpacity>
                )}
              </View>

              {shareModalToast ? (
                <View
                  style={[
                    styles.shareModalToast,
                    shareModalToast.type === 'error'
                      ? styles.feedbackToastError
                      : styles.feedbackToastSuccess,
                  ]}
                  {...(Platform.OS === 'web'
                    ? {
                        dataSet: {
                          routineBuilderShareModalToast: 'true',
                          routineBuilderShareModalToastType: shareModalToast.type,
                        },
                      }
                    : {})}
                >
                  <Text
                    style={styles.feedbackToastMessage}
                    {...(Platform.OS === 'web' ? { dataSet: { routineBuilderShareModalToastText: 'true' } } : {})}
                  >
                    {shareModalToast.message}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>
        </Modal>
      ) : null}

      {cloneTargetRoutine ? (
        <Modal visible transparent animationType="fade" onRequestClose={closeCloneModal}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>Clone routine</Text>
              <Text style={styles.modalBody}>
                Name your copy. “{CLONED_ROUTINE_PREFIX} ” will always stay at the start.
              </Text>
              <Text style={styles.label}>{CLONED_ROUTINE_PREFIX} routine name</Text>
              <View style={styles.cloneNameInputRow}>
                <Text style={styles.cloneNamePrefix}>{CLONED_ROUTINE_PREFIX} </Text>
                <TextInput
                  style={styles.cloneNameInput}
                  value={cloneNameSuffix}
                  onChangeText={setCloneNameSuffix}
                  placeholder="Routine name"
                  placeholderTextColor={theme.colors.textSecondary || '#888'}
                  editable={!cloningRoutine}
                />
              </View>
              <View style={styles.modalActions}>
                <TouchableOpacity
                  style={styles.secondaryBtn}
                  onPress={closeCloneModal}
                  disabled={cloningRoutine}
                >
                  <Text style={styles.secondaryBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryBtn, cloningRoutine && styles.btnDisabled]}
                  onPress={() => void handleCloneRoutineConfirm()}
                  disabled={cloningRoutine}
                >
                  <Text style={styles.primaryBtnText}>
                    {cloningRoutine ? 'Cloning…' : 'Save clone'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      ) : null}

      <Modal visible={showDeleteModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Delete routines?</Text>
            <Text style={styles.modalBody}>
              The following routines will be permanently deleted:
            </Text>
            {selectedDeleteNames.map((name) => (
              <Text key={name} style={styles.modalListItem}>
                • {name}
              </Text>
            ))}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.dangerBtn, deleting && styles.btnDisabled]}
                onPress={confirmDelete}
                disabled={deleting}
              >
                <Text style={styles.primaryBtnText}>{deleting ? 'Deleting…' : 'Confirm'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 40 },
    pageTitle: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 16,
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      flexWrap: 'wrap',
      gap: 8,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
    },
    sectionActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    iconBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    iconBtnActive: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    newRoutineBtn: {
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 12,
    },
    newRoutineBtnText: {
      color: '#FFFFFF',
      fontWeight: '600',
      fontSize: 13,
    },
    renameActions: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 16,
    },
    primaryBtn: {
      flex: 1,
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: 'center',
    },
    secondaryBtn: {
      flex: 1,
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: 'center',
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
    },
    dangerBtn: {
      flex: 1,
      backgroundColor: '#c62828',
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: 'center',
    },
    primaryBtnText: { color: '#FFFFFF', fontWeight: '600', fontSize: 15 },
    secondaryBtnText: {
      color: theme.colors.primary || '#d4af37',
      fontWeight: '600',
      fontSize: 15,
    },
    btnDisabled: { opacity: 0.6 },
    loader: { marginVertical: 24 },
    emptyListText: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.8,
      marginBottom: 16,
    },
    routineCard: { marginBottom: 20 },
    routineTitleCell: {
      flex: 1,
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 6,
      backgroundColor: 'transparent',
    },
    routineTitleHighlight: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: '#d4af37',
      borderRadius: 6,
    },
    routineCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 4,
    },
    routineCheckboxWrap: { paddingRight: 4 },
    routineCheckbox: {
      width: 24,
      height: 24,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
    },
    routineCheckboxSelected: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    checkmark: { color: '#FFFFFF', fontWeight: '700', fontSize: 14 },
    routineName: {
      flex: 1,
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.text,
      paddingVertical: 10,
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
    },
    routineNameInput: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 6,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: theme.isDarkMode ? '#000000' : '#e8e8e8',
    },
    editExercisesBtn: {
      paddingVertical: 8,
      paddingHorizontal: 8,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
    },
    routineBox: {
      padding: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#f9ffd6',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      ...(Platform.OS === 'web' && !theme.isDarkMode
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }
        : {}),
    },
    editPanel: {
      marginBottom: 24,
      padding: 16,
      borderRadius: 8,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fffef5',
    },
    editPanelTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 10,
    },
    label: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 6,
    },
    nameInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 15,
      color: theme.colors.text,
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? '#000000' : '#e8e8e8',
    },
    editActions: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 16,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    modalOutsideTap: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'transparent',
    },
    shareModalCard: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fffef5',
      borderRadius: 12,
      padding: 20,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      zIndex: 1,
    },
    shareModalHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 12,
      gap: 8,
      backgroundColor: 'transparent',
    },
    shareModalTitle: {
      flex: 1,
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    shareModalCloseIcon: {
      padding: 4,
      backgroundColor: 'transparent',
    },
    shareModalCloseIconText: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.text,
      lineHeight: 22,
      backgroundColor: 'transparent',
    },
    shareModalToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginTop: 12,
    },
    shareCloseBtn: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#555555' : '#9e9e9e',
    },
    shareCloseBtnText: {
      color: '#FFFFFF',
      fontWeight: '700',
      fontSize: 15,
      backgroundColor: 'transparent',
    },
    modalCard: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 8,
    },
    modalBody: {
      fontSize: 14,
      color: theme.colors.text,
      marginBottom: 8,
    },
    modalListItem: {
      fontSize: 14,
      color: theme.colors.text,
      marginLeft: 4,
      marginBottom: 4,
    },
    modalActions: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 16,
    },
    feedbackToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 16,
    },
    feedbackToastError: { backgroundColor: '#c62828' },
    feedbackToastSuccess: { backgroundColor: '#2e7d32' },
    feedbackToastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    feedbackToastClose: { padding: 4, backgroundColor: 'transparent' },
    feedbackToastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    sharedSectionHeader: { marginTop: 24 },
    sharedRoutineHeader: { marginBottom: 10 },
    sharedRoutineTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    sharedRoutineBadge: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    sharedRoutineMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
      backgroundColor: 'transparent',
    },
    routineNameInline: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    sharedRoutineOwnerInline: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      opacity: 0.85,
      backgroundColor: 'transparent',
    },
    iconActionBtn: {
      paddingVertical: 8,
      paddingHorizontal: 8,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    cloneBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    cloneBtnText: {
      color: '#FFFFFF',
      fontWeight: '700',
      fontSize: 13,
      backgroundColor: 'transparent',
    },
    sharePanelActions: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    shareDropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? '#000000' : '#e8e8e8',
    },
    shareDropdownTriggerText: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    shareDropdownArrow: {
      fontSize: 12,
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    shareDropdownList: {
      maxHeight: 180,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    shareDropdownItem: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    shareDropdownItemSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.15)' : 'rgba(212, 175, 55, 0.2)',
    },
    shareDropdownItemText: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    cloneNameInputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 6,
      paddingHorizontal: 10,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? '#000000' : '#e8e8e8',
    },
    cloneNamePrefix: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    cloneNameInput: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      paddingVertical: 8,
      backgroundColor: 'transparent',
    },
  });
